/**
 * Stato di rete «vero» dell'app: non solo navigator.onLine (che mente con Wi‑Fi senza internet o con
 * segnale debolissimo), ma anche l'esito reale delle richieste e una sonda leggera quando si è offline.
 *
 * - Un errore di rete (o un timeout) su una richiesta al backend → offline subito.
 * - Offline: una sonda ogni 4 s verso il backend; alla prima risposta → online.
 * - Allinea react-query (onlineManager) così al ritorno della rete le schermate si rinfrescano da sole.
 */
import { onlineManager } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { Network } from "@capacitor/network";

export interface NetState {
  online: boolean;
  /** ultimo istante in cui il backend ha risposto */
  lastOnlineAt: number | null;
}

type Listener = (s: NetState) => void;

const listeners = new Set<Listener>();
let state: NetState = {
  online: typeof navigator === "undefined" ? true : navigator.onLine !== false,
  lastOnlineAt: typeof navigator === "undefined" || navigator.onLine !== false ? Date.now() : null,
};
let probeTimer: ReturnType<typeof setInterval> | null = null;
let probing = false;
let started = false;
let probeUrl = "";
let probeKey = "";

function emit() {
  listeners.forEach((l) => {
    try {
      l(state);
    } catch {
      /* un listener non rompe gli altri */
    }
  });
}

function set(online: boolean) {
  if (state.online === online) {
    if (online) state = { ...state, lastOnlineAt: Date.now() };
    return;
  }
  state = { online, lastOnlineAt: online ? Date.now() : state.lastOnlineAt };
  try {
    onlineManager.setOnline(online);
  } catch {
    /* react-query non pronto: ignora */
  }
  if (online) stopProbing();
  else startProbing();
  emit();
}

async function probe(): Promise<boolean> {
  if (probing || !probeUrl) return state.online;
  probing = true;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 4000);
    try {
      // qualunque risposta HTTP (anche 401) prova che il backend è raggiungibile
      const res = await fetch(probeUrl, { method: "GET", headers: { apikey: probeKey }, cache: "no-store", signal: ctrl.signal });
      return res.status < 500;
    } finally {
      clearTimeout(t);
    }
  } catch {
    return false;
  } finally {
    probing = false;
  }
}

function startProbing() {
  if (probeTimer) return;
  probeTimer = setInterval(() => {
    void probe().then((ok) => {
      if (ok) set(true);
    });
  }, 2500);
}
function stopProbing() {
  if (probeTimer) clearInterval(probeTimer);
  probeTimer = null;
}

/**
 * «Qualcosa è cambiato» (rete nativa, app in primo piano, tocco): verifica subito e ancora a breve distanza,
 * perché appena torna il segnale la rete impiega qualche secondo a essere davvero utilizzabile.
 */
let burstTimers: ReturnType<typeof setTimeout>[] = [];
function burstProbe() {
  burstTimers.forEach(clearTimeout);
  burstTimers = [0, 1200, 3000, 6000].map((ms) =>
    setTimeout(() => {
      if (state.online && ms > 0) return; // già verificato: basta
      void probe().then((ok) => set(ok));
    }, ms),
  );
}

/** Da chiamare una volta (client Supabase): imposta l'URL di sonda e ascolta gli eventi del dispositivo. */
export function startNetMonitor(opts: { backendUrl: string; apiKey: string }) {
  if (started || typeof window === "undefined") return;
  started = true;
  probeUrl = `${opts.backendUrl}/auth/v1/health`;
  probeKey = opts.apiKey;
  window.addEventListener("offline", () => set(false));
  window.addEventListener("online", () => {
    // il dispositivo dice «c'è rete»: verifica che il backend risponda davvero
    burstProbe();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") burstProbe();
  });
  // un tocco mentre si è «offline» è il momento migliore per controllare di nuovo (max ogni 2 s)
  let lastTouchProbe = 0;
  window.addEventListener(
    "pointerdown",
    () => {
      if (state.online || Date.now() - lastTouchProbe < 2000) return;
      lastTouchProbe = Date.now();
      void probe().then((ok) => ok && set(true));
    },
    { capture: true, passive: true },
  );
  if (Capacitor.isNativePlatform()) {
    // segnale nativo (come NWPathMonitor): cambio Wi‑Fi/rete mobile, modalità aereo, ripresa dell'app
    void Network.addListener("networkStatusChange", (st) => {
      if (!st.connected) set(false);
      else burstProbe();
    }).catch(() => undefined);
    void Network.getStatus()
      .then((st) => {
        if (!st.connected) set(false);
      })
      .catch(() => undefined);
    void CapApp.addListener("appStateChange", (st) => {
      if (st.isActive) burstProbe();
    }).catch(() => undefined);
    void CapApp.addListener("resume", () => burstProbe()).catch(() => undefined);
  }
  if (!state.online) startProbing();
  else void probe().then((ok) => !ok && set(false));
}

export const getNetState = (): NetState => state;
export const isOnlineNow = (): boolean => state.online;
export const reportNetworkFailure = () => set(false);
export const reportNetworkSuccess = () => set(true);
export function subscribeNet(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
/** Forza una verifica immediata (es. tasto «Riprova»). */
export async function checkNetNow(): Promise<boolean> {
  const ok = await probe();
  set(ok);
  return ok;
}
